import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { StageIcon } from 'components'
import type { StageState } from 'components/StageIcon'
import { useAnalytics } from 'hooks'
import {
  AlertTriangle,
  ArrowRight,
  Instagram,
  Linkedin,
  Loader2,
  Video,
  Youtube,
} from 'lucide-react'
import { usePusher } from '../../core/contexts'
import { FacebookIcon, PinterestIcon } from 'components'
import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { reconcileCreativeStatus, useGenerationStatusStore } from '../store/generation-status-store'
import { useCreativeStore } from '../store/creative-store'
import { XLogo } from './XLogo'

// Channel identity rides on the icon's `accent` colour only; the card
// chrome stays unified across every type (no per-channel gradient bg).
const BASE_CHROME = {
  bg: 'bg-bg-2',
  border: 'border-line',
  iconBg: 'bg-bg-3',
  shimmer: 'from-bg-3 via-bg-2 to-bg-3',
} as const

type TypeConfig = typeof BASE_CHROME & {
  icon: React.FC<{ size?: number; className?: string }>
  label: string
  accent: string
}

const TYPE_CONFIG: Record<string, TypeConfig> = {
  instagram: { ...BASE_CHROME, icon: Instagram, label: 'Instagram Post', accent: 'text-[#E4405F]' },
  facebook: {
    ...BASE_CHROME,
    icon: FacebookIcon,
    label: 'Facebook Post',
    accent: 'text-[#1877F2]',
  },
  tiktok: { ...BASE_CHROME, icon: Video, label: 'TikTok Video', accent: 'text-paper' },
  linkedin: { ...BASE_CHROME, icon: Linkedin, label: 'LinkedIn Post', accent: 'text-[#0A66C2]' },
  x: { ...BASE_CHROME, icon: XLogo, label: 'X Post', accent: 'text-paper' },
  youtube_shorts: {
    ...BASE_CHROME,
    icon: Youtube,
    label: 'YouTube Short',
    accent: 'text-[#FF0033]',
  },
  pinterest: {
    ...BASE_CHROME,
    icon: PinterestIcon,
    label: 'Pinterest Pin',
    accent: 'text-[#BD081C]',
  },
}

const DEFAULT_CONFIG = TYPE_CONFIG.instagram

// Module-level stable references so the zustand selector + useMemo deps
// below don't get a fresh allocation per render (which would break
// Object.is equality and force re-renders on every store update during
// streaming).
const EMPTY_FIELDS: ReadonlySet<string> = new Set()
const EMPTY_STAGES: ReadonlyArray<{ id: string; label: string }> = Object.freeze([])

// ─── Per-channel stage ladders ─────────────────────────────────────────────
//
// Maps each creative type to its expected user-visible fields. The
// CreativeGenerationCard uses these to render a stage-marker feed (as in
// onboarding's ScanningStep) instead of a single "Preparing..." spinner —
// so the user sees the AI's progress field-by-field rather than an opaque
// loading state. Field IDs match what the server emits via the
// `generatingFields` set on the creative-store. As each field is removed
// from the set (server stops generating it), the stage flips to "done";
// new fields appearing flip to "active".
const FIELD_STAGES: Record<string, ReadonlyArray<{ id: string; label: string }>> = {
  instagram: [
    { id: 'image', label: 'Designing the image' },
    { id: 'caption', label: 'Writing the caption' },
    { id: 'hashtags', label: 'Picking hashtags' },
  ],
  facebook: [
    { id: 'image', label: 'Designing the image' },
    { id: 'message', label: 'Writing the post' },
  ],
  linkedin: [
    { id: 'image', label: 'Designing the image' },
    { id: 'caption', label: 'Writing the post' },
    { id: 'hashtags', label: 'Picking hashtags' },
  ],
  tiktok: [
    { id: 'video', label: 'Generating the video' },
    { id: 'caption', label: 'Writing the caption' },
    { id: 'hashtags', label: 'Picking hashtags' },
  ],
  youtube_shorts: [
    { id: 'video', label: 'Generating the short' },
    { id: 'title', label: 'Writing the title' },
    { id: 'description', label: 'Writing the description' },
  ],
  pinterest: [
    { id: 'image', label: 'Designing the pin' },
    { id: 'title', label: 'Writing the title' },
    { id: 'description', label: 'Writing the description' },
  ],
}

/** Has the field landed on the creative's content yet? */
function hasFieldContent(creative: { content?: unknown } | undefined, field: string): boolean {
  const c = creative?.content as Record<string, unknown> | undefined
  if (!c) return false
  switch (field) {
    case 'caption':
      return typeof c.caption === 'string' && c.caption.length > 0
    case 'message':
      return typeof c.message === 'string' && c.message.length > 0
    case 'image':
      return !!c.imageUrl || !!c.imageAttachmentId
    case 'video':
      return !!c.videoUrl
    case 'hashtags':
      return Array.isArray(c.hashtags) && (c.hashtags as unknown[]).length > 0
    case 'title':
      return typeof c.title === 'string' && c.title.length > 0
    case 'description':
      return typeof c.description === 'string' && c.description.length > 0
    default:
      return false
  }
}

interface CreativeGenerationCardProps {
  creativeId: string
  type: string
  name: string
  initialStatus: 'generating' | 'created' | 'failed'
  /**
   * Project ID for the wrapper Post that owns this creative. Required since
   * every creative now lives under a Post project; the composer always opens
   * at `/compose/<projectId>/<platform>` so users can switch platform tabs.
   */
  projectId: string
  /**
   * Platform tab to open inside the composer (matches the creative's type).
   */
  platform: string
}

export function CreativeGenerationCard({
  creativeId,
  type,
  name,
  initialStatus,
  projectId,
  platform,
}: CreativeGenerationCardProps) {
  const composerUrl = `/compose/${projectId}/${platform}`
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const creative = useCreativeStore(s => s.creatives.find(c => c.id === creativeId))

  const storeStatus = useGenerationStatusStore(s => s.statuses[creativeId])
  const mountTimeRef = useRef(Date.now())
  const hasTrackedViewRef = useRef(false)
  const prevStatusRef = useRef<string | null>(null)
  const { isConnected: isPusherConnected } = usePusher()
  const prevPusherConnectedRef = useRef(isPusherConnected)

  // First-mount reconciliation: only meaningful for cards that were started
  // generating. Skip if the store already has a terminal entry (e.g. because
  // a Pusher event arrived first this session).
  useEffect(() => {
    if (initialStatus !== 'generating' || storeStatus) return
    return reconcileCreativeStatus(creativeId)
  }, [creativeId, initialStatus, storeStatus])

  // Reconciliation when Pusher reconnects after a drop. Pusher private
  // channels do not buffer events, so any CreativeGenerationComplete fired
  // while the socket was reconnecting is lost; the DB still has the truth.
  useEffect(() => {
    const justReconnected = !prevPusherConnectedRef.current && isPusherConnected
    prevPusherConnectedRef.current = isPusherConnected
    if (!justReconnected) return
    if (initialStatus !== 'generating' || storeStatus) return
    return reconcileCreativeStatus(creativeId)
  }, [creativeId, isPusherConnected, initialStatus, storeStatus])

  // Resolve effective status — DB-backed store wins; chat tool output is the
  // initial render state until reconciliation completes.
  let status: 'generating' | 'ready' | 'failed' | 'created'
  let imageUrl: string | undefined

  if (storeStatus) {
    status = storeStatus.status
    imageUrl = storeStatus.imageUrl
  } else if (initialStatus === 'failed') {
    status = 'failed'
  } else if (initialStatus === 'created') {
    status = 'created'
  } else {
    status = 'generating'
  }

  // Track card viewed on mount
  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true
      track(AnalyticsEvents.Creatives.CREATIVE_GENERATION_CARD_VIEWED, {
        creative_id: creativeId,
        creative_type: type,
      })
    }
  }, [creativeId, type, track])

  // Track status changes (generating → ready/failed)
  useEffect(() => {
    if (
      prevStatusRef.current &&
      prevStatusRef.current !== status &&
      (status === 'ready' || status === 'failed')
    ) {
      track(AnalyticsEvents.Creatives.CREATIVE_GENERATION_STATUS_CHANGED, {
        creative_id: creativeId,
        creative_type: type,
        new_status: status,
        time_since_started_ms: Date.now() - mountTimeRef.current,
      })
    }
    prevStatusRef.current = status
  }, [status, creativeId, type, track])

  const config = TYPE_CONFIG[type] || DEFAULT_CONFIG
  const Icon = config.icon
  const displayName = storeStatus?.name || name
  const isClickable = status !== 'generating'

  // Only the first in-flight field is "active" so the cursor stays
  // focal; later in-flight fields read as pending until they start
  // streaming. Done = has content AND no longer in the generating set.
  const generatingFields = useCreativeStore(s =>
    creative ? (s.generatingFields.get(creative.id) ?? EMPTY_FIELDS) : EMPTY_FIELDS
  )
  const stageLadder = FIELD_STAGES[type] ?? EMPTY_STAGES
  const showStageFeed = status === 'generating' && stageLadder.length > 0
  const stageRows = useMemo(() => {
    let foundActive = false
    return stageLadder.map(stage => {
      const inFlight = generatingFields.has(stage.id)
      const hasContent = hasFieldContent(creative, stage.id)
      let state: StageState
      if (hasContent && !inFlight) state = 'done'
      else if (inFlight && !foundActive) {
        state = 'active'
        foundActive = true
      } else state = 'pending'
      return { ...stage, state }
    })
  }, [stageLadder, generatingFields, creative])

  const handleCardClick = () => {
    if (!isClickable) return
    track(AnalyticsEvents.Creatives.CREATIVE_GENERATION_CARD_TAPPED, {
      creative_id: creativeId,
      creative_type: type,
      card_status: status as 'ready' | 'created',
    })
    navigate(composerUrl, { state: { source: 'assistant', entry_source: 'assistant_chat' } })
  }

  return (
    <div className="my-3 w-full">
      <div
        className={`group w-full overflow-hidden rounded-[12px] border text-left transition-all duration-300 ${
          status === 'failed'
            ? 'border-[color:color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--warning)_8%,transparent)]'
            : `${config.border} ${config.bg}`
        }`}
      >
        {/* Pool chrome progress strip — one sliver across every channel while generating. */}
        {status === 'generating' && (
          <div className="bg-acid-tint h-[2px] w-full overflow-hidden">
            <div className="bg-accent-chrome h-full w-1/3 animate-pulse motion-reduce:animate-none" />
          </div>
        )}

        {/* Image preview when ready */}
        {imageUrl && status === 'ready' && (
          <button onClick={handleCardClick} className="block h-36 w-full overflow-hidden">
            <img
              src={imageUrl}
              alt={displayName}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </button>
        )}

        <button
          data-vaul-no-drag
          onClick={handleCardClick}
          disabled={!isClickable}
          className={`flex w-full items-center gap-3.5 p-4 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] border border-line-bright ${config.iconBg} transition-transform duration-300 ${isClickable ? 'group-hover:scale-105' : ''}`}
          >
            <Icon size={20} className={config.accent} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold text-paper">{displayName}</p>
            {status === 'generating' && (
              <div className="mt-1 flex items-center gap-1.5">
                {!showStageFeed && (
                  <>
                    <Loader2 size={13} className="animate-spin text-acid" />
                    <span className="text-[12px] text-paper-dim">
                      Preparing your {config.label.toLowerCase()}...
                    </span>
                  </>
                )}
                {showStageFeed && (
                  <span className="text-[12px] text-paper-dim">
                    Building your {config.label.toLowerCase()}.
                  </span>
                )}
              </div>
            )}
            {status === 'failed' && (
              <div className="mt-1 flex items-center gap-1.5">
                <AlertTriangle size={13} className="text-[color:var(--warning)]" />
                <span className="text-[12px] text-[color:var(--warning)]">
                  {storeStatus?.error === 'insufficient_credits'
                    ? 'Not enough credits'
                    : storeStatus?.error === 'needs_proof_screenshot'
                      ? "Needs the customer's screenshot"
                      : storeStatus?.error === 'needs_product_photo'
                        ? 'Needs a photo of the product'
                        : storeStatus?.error === 'needs_more_brand_info'
                          ? 'Needs more to work with'
                          : 'Something went wrong'}
                </span>
              </div>
            )}
          </div>
          {isClickable && (
            <div
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-line-bright ${
                status === 'failed'
                  ? 'bg-[color:color-mix(in_srgb,var(--warning)_8%,transparent)] text-[color:var(--warning)]'
                  : `${config.iconBg} ${config.accent}`
              } transition-transform duration-200 group-hover:scale-110`}
            >
              <ArrowRight size={16} />
            </div>
          )}
        </button>

        {/* Per-field stage feed during generation. Tick-over reads
            field-by-field so the user sees the AI's actual progress
            ("Designing the image" → done, "Writing the caption" → active,
            "Picking hashtags" → pending) instead of an opaque spinner. */}
        {showStageFeed && (
          <ul className="flex flex-col gap-3 border-t border-line px-4 py-3">
            {stageRows.map(stage => (
              <li key={stage.id} className="flex min-h-[20px] items-center gap-2.5">
                <StageIcon
                  state={stage.state}
                  label={`${stage.label}, ${stage.state === 'done' ? 'done' : stage.state === 'active' ? 'in progress' : 'pending'}`}
                />
                <span
                  className={
                    stage.state === 'active'
                      ? 'text-[12.5px] font-medium text-paper'
                      : stage.state === 'done'
                        ? 'text-[12.5px] text-paper-dim'
                        : 'text-[12.5px] text-paper-faint'
                  }
                >
                  {stage.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
